import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import BookingSummary from '@/components/bookings/BookingSummary';
import DelegatePicker from '@/components/bookings/DelegatePicker';
import {
  AlertCircle,
  Loader2,
} from 'lucide-react';

const ConfirmBookingDialog = ({
  open,
  onClose,
  booking,
  quoteOption,
  onSuccess
}) => {
  const { user, userProfile } = useAuth();
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [conflicts, setConflicts] = useState({ blocked: [], warnings: [] });

  useEffect(() => {
    if (open && booking) {
      fetchDelegates();
    }
  }, [open, booking]);

  // Run validation when delegates or quote option changes
  useEffect(() => {
    if (delegates.length > 0 && !delegates.some(d => d.unmatched) && quoteOption) {
      validateDelegates();
    } else if (delegates.length === 0) {
      setConflicts({ blocked: [], warnings: [] });
    }
  }, [delegates, quoteOption]);

  const fetchDelegates = async () => {
    if (!booking?.project_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Match existing delegate_names to space members
      if (booking.delegate_names && booking.delegate_names.length > 0) {
        const { data: allSpaceMembers } = await supabase
          .from('space_members')
          .select(`
            id, role, user_id,
            user:user_profiles!space_members_user_id_fkey (
              id, display_name, first_name, last_name, email, avatar_url
            )
          `)
          .eq('project_id', booking.project_id);

        const matchedDelegates = [];

        for (const delegateName of booking.delegate_names) {
          const match = allSpaceMembers?.find(sm => {
            const displayName = sm.user?.display_name || '';
            const fullName = `${sm.user?.first_name || ''} ${sm.user?.last_name || ''}`.trim();
            return (
              displayName.toLowerCase().trim() === delegateName.toLowerCase().trim() ||
              fullName.toLowerCase().trim() === delegateName.toLowerCase().trim()
            );
          });

          if (match) {
            matchedDelegates.push({
              id: match.id,
              space_member_id: match.id,
              name: match.user?.display_name || delegateName,
              display_name: match.user?.display_name,
              email: match.user?.email,
              avatar_url: match.user?.avatar_url,
              role: match.role,
              user_id: match.user?.id
            });
          } else {
            matchedDelegates.push({
              id: `unmatched-${delegateName}`,
              name: delegateName,
              display_name: delegateName,
              unmatched: true
            });
          }
        }

        setDelegates(matchedDelegates);
      } else {
        setDelegates([]);
      }
    } catch (error) {
      console.error('Error fetching delegates:', error);
      toast.error('Failed to load delegate information');
    } finally {
      setLoading(false);
    }
  };

  const ensureSpaceMember = async (member) => {
    // If the member is already a space member (has a project-level id), return as-is
    if (!member._isCompanyMember) return member;

    const userId = member.user_id || member.user_profiles?.id;
    if (!userId) return member;

    const { data, error } = await supabase
      .from('space_members')
      .insert({
        project_id: booking.project_id,
        user_id: userId,
        role: 'Member',
        status: 'active',
        show_on_matrix: true,
        last_active: new Date().toISOString(),
        sites: 1,
        created_date: new Date().toISOString()
      })
      .select('id, user_id, role')
      .single();

    if (error) {
      console.warn('Failed to create space member:', error);
      return member;
    }

    return { ...member, id: data.id, space_member_id: data.id, _isCompanyMember: false };
  };

  const handleAddDelegate = async (member) => {
    const spaceMember = await ensureSpaceMember(member);
    const profile = spaceMember.user_profiles || spaceMember.user || {};
    setDelegates(prev => [...prev, {
      id: spaceMember.id,
      space_member_id: spaceMember.id,
      name: profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
      display_name: profile.display_name,
      email: profile.email,
      avatar_url: profile.avatar_url,
      role: spaceMember.role,
      user_id: profile.id || spaceMember.user_id
    }]);
  };

  const handleRemoveDelegate = (delegate) => {
    setDelegates(prev => prev.filter(d =>
      d.id !== delegate.id && d.space_member_id !== delegate.space_member_id
    ));
  };

  const validateDelegates = async () => {
    if (!booking?.course_name || !delegates || delegates.length === 0) {
      return;
    }

    try {
      const spaceMemberIds = delegates
        .filter(d => !d.unmatched && d.space_member_id)
        .map(d => d.space_member_id);

      if (spaceMemberIds.length === 0) return;

      // Run all checks in parallel
      const [duplicateBookings, completedTraining, leaveConflicts] = await Promise.all([
        checkDuplicateBookings(spaceMemberIds, booking.course_name),
        checkCompletedTraining(spaceMemberIds, booking.course_name),
        checkLeaveConflicts(spaceMemberIds, quoteOption?.dates || [])
      ]);

      // Build blocked list (hard blocks that prevent booking)
      const blocked = [];

      // Add delegates with duplicate bookings
      duplicateBookings.forEach(conflict => {
        const delegate = delegates.find(d => d.space_member_id === conflict.space_member_id);
        if (delegate) {
          blocked.push({
            ...delegate,
            conflict: {
              type: 'duplicate_booking',
              message: `Already booked: ${conflict.course_name} on ${formatDate(conflict.booked_date)}`,
              bookingId: conflict.booking_id,
              bookingDate: conflict.booked_date
            }
          });
        }
      });

      // Add delegates with completed training
      completedTraining.forEach(conflict => {
        const delegate = delegates.find(d => d.space_member_id === conflict.space_member_id);
        if (delegate && !blocked.find(b => b.id === delegate.id)) {
          const expiryText = conflict.expiry_date
            ? `valid until ${formatDate(conflict.expiry_date)}`
            : 'no expiry';

          blocked.push({
            ...delegate,
            conflict: {
              type: 'already_completed',
              message: `Already completed: ${conflict.requirement_name} (${expiryText})`,
              completedDate: conflict.completed_date,
              expiryDate: conflict.expiry_date
            }
          });
        }
      });

      // Build warnings list (soft warnings that allow booking to proceed)
      const warnings = [];

      // Add delegates with leave conflicts
      leaveConflicts.forEach(conflict => {
        const delegate = delegates.find(d => d.space_member_id === conflict.space_member_id);
        if (delegate) {
          warnings.push({
            delegate,
            leave_type: conflict.leave_type,
            start_date: conflict.start_date,
            end_date: conflict.end_date
          });
        }
      });

      setConflicts({ blocked, warnings });
    } catch (error) {
      console.error('Error validating delegates:', error);
      toast.error('Failed to check for booking conflicts');
    }
  };

  const checkDuplicateBookings = async (spaceMemberIds, courseName) => {
    const { data, error } = await supabase
      .from('booking_delegates')
      .select(`
        id,
        space_member_id,
        training_quote:training_quotes!inner (
          id,
          course_name,
          booked_date,
          status
        )
      `)
      .in('space_member_id', spaceMemberIds)
      .eq('training_quote.course_name', courseName)
      .in('training_quote.status', ['booked', 'live'])
      .neq('attendance_status', 'cancelled');

    if (error) {
      console.error('Error checking duplicate bookings:', error);
      return [];
    }

    return (data || []).map(item => ({
      space_member_id: item.space_member_id,
      booking_id: item.training_quote.id,
      course_name: item.training_quote.course_name,
      booked_date: item.training_quote.booked_date,
      status: item.training_quote.status
    }));
  };

  const checkCompletedTraining = async (spaceMemberIds, courseName) => {
    const { data: spaceMembersData, error: spaceMembersError } = await supabase
      .from('space_members')
      .select('id, user_id')
      .in('id', spaceMemberIds);

    if (spaceMembersError || !spaceMembersData) {
      console.error('Error fetching space members for validation:', spaceMembersError);
      return [];
    }

    const userIds = spaceMembersData.map(sm => sm.user_id).filter(Boolean);

    if (userIds.length === 0) return [];

    const { data: teamMembers, error: teamMembersError } = await supabase
      .from('team_members')
      .select('id, user_id')
      .eq('project_id', booking.project_id)
      .in('user_id', userIds);

    if (teamMembersError || !teamMembers || teamMembers.length === 0) {
      return [];
    }

    const teamMemberIds = teamMembers.map(tm => tm.id);

    const { data, error } = await supabase
      .from('team_member_requirements')
      .select(`
        id,
        team_member_id,
        status,
        completed_date,
        expiry_date,
        requirement:requirements_library!inner (
          id,
          name
        ),
        team_member:team_members!inner (
          user_id
        )
      `)
      .in('team_member_id', teamMemberIds)
      .eq('status', 'completed');

    if (error) {
      console.error('Error checking completed training:', error);
      return [];
    }

    const now = new Date();
    const courseNameLower = courseName.toLowerCase();

    return (data || [])
      .filter(item => {
        const requirementNameLower = item.requirement.name.toLowerCase();
        const isMatch = courseNameLower.includes(requirementNameLower) ||
                       requirementNameLower.includes(courseNameLower);

        if (!isMatch) return false;

        if (!item.expiry_date) return true;
        return new Date(item.expiry_date) > now;
      })
      .map(item => {
        const spaceMember = spaceMembersData.find(sm => sm.user_id === item.team_member.user_id);
        return {
          space_member_id: spaceMember?.id,
          requirement_name: item.requirement.name,
          completed_date: item.completed_date,
          expiry_date: item.expiry_date
        };
      })
      .filter(item => item.space_member_id);
  };

  const checkLeaveConflicts = async (spaceMemberIds, trainingDates) => {
    if (!trainingDates || trainingDates.length === 0) {
      return [];
    }

    try {
      const { data: spaceMembersData, error: spaceMembersError } = await supabase
        .from('space_members')
        .select('id, user_id')
        .in('id', spaceMemberIds);

      if (spaceMembersError || !spaceMembersData) {
        console.error('Error fetching space members for leave check:', spaceMembersError);
        return [];
      }

      const userIds = spaceMembersData.map(sm => sm.user_id).filter(Boolean);
      if (userIds.length === 0) return [];

      const trainingDateStrings = trainingDates.map(d => d.startDate).filter(Boolean);
      if (trainingDateStrings.length === 0) return [];

      const trainingStartDate = new Date(Math.min(...trainingDateStrings.map(d => new Date(d))));
      const trainingEndDate = new Date(Math.max(...trainingDateStrings.map(d => new Date(d))));

      const { data: leaveRequests, error: leaveError } = await supabase
        .from('leave_requests')
        .select('id, user_id, leave_type, start_date, end_date')
        .in('user_id', userIds)
        .eq('status', 'approved')
        .lte('start_date', trainingEndDate.toISOString().split('T')[0])
        .gte('end_date', trainingStartDate.toISOString().split('T')[0]);

      if (leaveError) {
        console.error('Error checking leave conflicts:', leaveError);
        return [];
      }

      return (leaveRequests || []).map(leave => {
        const spaceMember = spaceMembersData.find(sm => sm.user_id === leave.user_id);
        return {
          space_member_id: spaceMember?.id,
          leave_type: leave.leave_type,
          start_date: leave.start_date,
          end_date: leave.end_date
        };
      }).filter(item => item.space_member_id);
    } catch (error) {
      console.error('Error checking leave conflicts:', error);
      return [];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatLeaveType = (type) => {
    const typeMap = {
      'annual_leave': 'Annual Leave',
      'sick_leave': 'Sick Leave',
      'unpaid_leave': 'Unpaid Leave',
      'work_from_home': 'WFH',
      'compassionate_leave': 'Compassionate Leave',
      'parental_leave': 'Parental Leave',
      'training': 'Training'
    };
    return typeMap[type] || type;
  };

  const handleConfirmBooking = async () => {
    if (!quoteOption || !booking) {
      toast.error('Missing booking information');
      return;
    }

    // Check for unmatched delegates (only if there are delegates)
    const unmatchedDelegates = delegates.filter(d => d.unmatched);
    if (unmatchedDelegates.length > 0) {
      toast.error(
        `Cannot book: ${unmatchedDelegates.length} delegate(s) could not be matched to space members`
      );
      return;
    }

    // Check for conflicts (blocked delegates)
    if (conflicts.blocked && conflicts.blocked.length > 0) {
      toast.error(
        `Cannot book: ${conflicts.blocked.length} delegate(s) have booking conflicts`
      );
      return;
    }

    try {
      setConfirming(true);

      const matchedDelegates = delegates.filter(d => !d.unmatched);

      // 1. Update quote_options status to booked
      const { error: optionError } = await supabase
        .from('quote_options')
        .update({ status: 256 })
        .eq('id', quoteOption.id);

      if (optionError) throw optionError;

      // 2. Update training_quotes with booking details and sync delegate_names
      const { error: quoteError } = await supabase
        .from('training_quotes')
        .update({
          status: 'booked',
          booking_confirmed_date: new Date().toISOString(),
          booked_quote_id: quoteOption.id,
          booking_confirmed_by: userProfile?.id,
          booked_date: quoteOption.dates?.[0]?.startDate || null,
          delegate_names: matchedDelegates.length > 0
            ? matchedDelegates.map(d => d.name || d.display_name)
            : booking.delegate_names || [],
          updated_date: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (quoteError) throw quoteError;

      // 3. Create or update booking_delegates records for each matched delegate
      if (matchedDelegates.length > 0) {
        const delegateRecords = matchedDelegates.map(delegate => ({
          training_quote_id: booking.id,
          space_member_id: delegate.space_member_id,
          attendance_status: 'pending',
          completion_status: 'not_started',
          created_by: userProfile?.id,
          created_date: new Date().toISOString()
        }));

        const { error: delegatesError } = await supabase
          .from('booking_delegates')
          .upsert(delegateRecords, {
            onConflict: 'training_quote_id,space_member_id',
            ignoreDuplicates: false
          });

        if (delegatesError) throw delegatesError;
      }

      // 4. Update compliance_assignments status to 'booked' for matched delegates
      if (matchedDelegates.length > 0 && booking.requirement_id && booking.project_id) {
        const userIds = matchedDelegates.map(d => d.user_id).filter(Boolean);
        if (userIds.length > 0) {
          for (const userId of userIds) {
            await supabase
              .from('compliance_assignments')
              .upsert({
                project_id: booking.project_id,
                requirement_id: booking.requirement_id,
                team_member_id: userId,
                assigned: true,
                status: 'booked',
                quote_id: booking.id
              }, {
                onConflict: 'project_id,requirement_id,team_member_id'
              });
          }
        }
      }

      // 5. Update user_qualifications to mark renewals as booked
      if (matchedDelegates.length > 0) {
        const { data: spaceMembersData } = await supabase
          .from('space_members')
          .select('user_id')
          .in('id', matchedDelegates.map(d => d.space_member_id));

        const userIds = spaceMembersData?.map(sm => sm.user_id).filter(Boolean) || [];

        if (userIds.length > 0) {
          if (booking.course_id) {
            const { error: qualificationsError } = await supabase
              .from('user_qualifications')
              .update({
                quote_id: booking.id,
                renewal_status: 'booked',
                updated_date: new Date().toISOString()
              })
              .in('user_id', userIds)
              .eq('course_link_id', booking.course_id.toString())
              .eq('renewal_status', 'quoting');

            if (qualificationsError) {
              console.warn('Failed to update user_qualifications by course_link_id:', qualificationsError);
            }
          }

          if (booking.requirement_id) {
            const { error: reqQualError } = await supabase
              .from('user_qualifications')
              .update({
                quote_id: booking.id,
                renewal_status: 'booked',
                updated_date: new Date().toISOString()
              })
              .in('user_id', userIds)
              .eq('requirement_id', booking.requirement_id)
              .eq('renewal_status', 'quoting');

            if (reqQualError) {
              console.warn('Failed to update user_qualifications by requirement_id:', reqQualError);
            }
          }
        }
      }

      // Success!
      const delegateText = matchedDelegates.length > 0
        ? `for ${matchedDelegates.length} delegate(s)`
        : '(no delegates assigned yet)';
      toast.success(
        `Training booked successfully ${delegateText}`,
        {
          description: `${booking.course_name} confirmed`,
        }
      );

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast.error('Failed to confirm booking', {
        description: error.message
      });
    } finally {
      setConfirming(false);
    }
  };

  const canConfirm = () => {
    // If delegates are selected, check for issues
    if (delegates.length > 0) {
      const hasUnmatched = delegates.some(d => d.unmatched);
      if (hasUnmatched) return false;

      if (conflicts.blocked && conflicts.blocked.length > 0) return false;
    }

    // Must not be already confirming
    if (confirming || loading) return false;

    return true;
  };

  const getConfirmButtonText = () => {
    if (confirming) return 'Booking...';

    const unmatchedCount = delegates.filter(d => d.unmatched).length;
    if (unmatchedCount > 0) {
      return `Cannot Book (${unmatchedCount} unmatched)`;
    }

    if (conflicts.blocked && conflicts.blocked.length > 0) {
      return `Cannot Book (${conflicts.blocked.length} conflicts)`;
    }

    if (delegates.length === 0) {
      return 'Confirm Booking (No Delegates)';
    }

    return 'Confirm Booking';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm Training Booking</DialogTitle>
          <DialogDescription>
            Review the booking details and assign delegates before confirming
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Booking Summary */}
          <BookingSummary
            courseName={quoteOption?.course_name || booking?.course_name}
            dates={quoteOption?.dates}
            location={quoteOption?.onsite_postcode}
            duration={quoteOption?.course_duration}
            cost={quoteOption?.quote_value}
            delivery={quoteOption?.course_delivery}
            provider={quoteOption?.trainers?.trainer_name}
          />

          {/* Two-Panel Delegate Picker */}
          {!loading && (
            <DelegatePicker
              booking={booking}
              assignedDelegates={delegates}
              onAdd={handleAddDelegate}
              onRemove={handleRemoveDelegate}
            />
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )}

          {/* Blocked Conflicts Alert */}
          {conflicts.blocked && conflicts.blocked.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cannot Book - Booking Conflicts Detected</AlertTitle>
              <AlertDescription>
                {conflicts.blocked.length} delegate(s) cannot be booked:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {conflicts.blocked.map((blocked, idx) => (
                    <li key={idx}>
                      <strong>{blocked.name || blocked.display_name}:</strong>{' '}
                      {blocked.conflict?.type === 'duplicate_booking'
                        ? 'Already booked for this course'
                        : 'Already completed this training'}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Unmatched Delegates Warning */}
          {delegates.some(d => d.unmatched) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cannot Book - Unmatched Delegates</AlertTitle>
              <AlertDescription>
                {delegates.filter(d => d.unmatched).length} delegate(s) are not space members.
                Add them to the project first.
              </AlertDescription>
            </Alert>
          )}

          {/* Warnings */}
          {conflicts.warnings && conflicts.warnings.length > 0 && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-900">Leave Conflicts Detected</AlertTitle>
              <AlertDescription className="text-yellow-700">
                {conflicts.warnings.length} delegate(s) have approved leave during training dates:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {conflicts.warnings.map((warning, idx) => (
                    <li key={idx}>
                      <strong>{warning.delegate?.name || warning.delegate?.display_name}:</strong>{' '}
                      {formatLeaveType(warning.leave_type)} ({formatDate(warning.start_date)}
                      {warning.start_date !== warning.end_date && ` - ${formatDate(warning.end_date)}`})
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-sm">
                  You can still book, but please coordinate with these delegates about their leave.
                </p>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBooking}
            disabled={!canConfirm()}
          >
            {confirming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {getConfirmButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmBookingDialog;
